import React, { useState, useEffect, useCallback } from 'react';

const PHONE_NUMBER = '+97517242482';
const API_BASE = `${process.env.REACT_APP_API_URL}/api/airport`;
const HEADERS = {
  Accept: 'application/json',
  'X-API-Key': process.env.REACT_APP_VEHICLES_API_KEY || '',
};

function AirportServiceViewer() {
  const [cars, setCars] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const [carsRes, notesRes] = await Promise.all([
        fetch(`${API_BASE}/cars`, { signal: controller.signal, headers: HEADERS, mode: 'cors' }),
        fetch(`${API_BASE}/notes`, { signal: controller.signal, headers: HEADERS, mode: 'cors' }),
      ]);
      clearTimeout(timeoutId);
      if (!carsRes.ok) throw new Error(`HTTP error! Status: ${carsRes.status}`);
      const carsData = await carsRes.json();
      const notesData = await notesRes.json();
      if (carsData.success) setCars(carsData.data);
      if (notesData.success) setNotes(notesData.data);
    } catch (err) {
      if (err.name === 'AbortError') setError('Request timed out');
      else setError(err.message || 'Failed to load airport service');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fetchAll]);

  const formatRate = (rate) => {
    if (!rate || String(rate).trim() === '') return 'Contact for pricing';
    const cleaned = String(rate).replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? rate : `Nu. ${num.toLocaleString()} + GST`;
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, #0F1419, #1A1F2E)', color: '#C7B381', fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: '300', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      Loading airport services...
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, #0F1419, #1A1F2E)', color: '#ff6b6b', textAlign: 'center', padding: '2rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h2 style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', marginBottom: '1rem' }}>⚠️ Error</h2>
      <p style={{ fontSize: 'clamp(1rem, 3vw, 1.3rem)', maxWidth: '600px' }}>{error}</p>
      <button onClick={fetchAll} style={{ marginTop: '2rem', padding: '1rem 2.5rem', background: '#C7B381', color: '#0F1419', border: 'none', borderRadius: '8px', fontSize: '1.1rem', fontWeight: '600', cursor: 'pointer' }}>Retry</button>
    </div>
  );

  return (
    <>
      {/* Floating Buttons */}
      <div style={{ position: 'fixed', right: 'clamp(15px, 3vw, 30px)', bottom: 'clamp(15px, 3vw, 30px)', zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 2vw, 15px)' }}>
        <a href={`tel:${PHONE_NUMBER}`} style={{ width: 'clamp(50px, 10vw, 60px)', height: 'clamp(50px, 10vw, 60px)', background: '#4CAF50', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 30px rgba(76,175,80,0.4)', transition: 'all 0.3s', fontSize: 'clamp(1.5rem, 4vw, 2rem)', textDecoration: 'none' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
          📞
        </a>
        <a href={`https://wa.me/${PHONE_NUMBER.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
          style={{ width: 'clamp(50px, 10vw, 60px)', height: 'clamp(50px, 10vw, 60px)', background: '#25D366', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 30px rgba(37,211,102,0.4)', transition: 'all 0.3s', textDecoration: 'none' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
          <svg viewBox="0 0 24 24" width="60%" height="60%" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.884 3.088" />
          </svg>
        </a>
      </div>

      <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #0F1419, #1A1F2E)', color: '#E8E8E8', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        <div style={{ padding: 'clamp(3rem, 8vw, 6rem) clamp(1rem, 4vw, 2rem) clamp(4rem, 10vw, 8rem)', maxWidth: '1600px', margin: '0 auto' }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(3rem, 8vw, 6rem)' }}>
            {/* Airport icon badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.2rem', background: 'rgba(199, 179, 129, 0.1)', border: '1px solid rgba(199, 179, 129, 0.3)', borderRadius: '30px', marginBottom: '1.5rem', fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', color: '#C7B381', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600' }}>
              ✈️ &nbsp; Pick-up & Drop-off Service
            </div>
            <h1 style={{ fontSize: 'clamp(2.5rem, 8vw, 4rem)', fontWeight: '300', marginBottom: '1.5rem', color: '#FFFFFF', letterSpacing: '-0.01em' }}>
              Airport <span style={{ fontWeight: '600', color: '#C7B381' }}>Transfer</span>
            </h1>
            <p style={{ fontSize: 'clamp(1rem, 3vw, 1.2rem)', color: '#B8B8B8', maxWidth: '700px', margin: '0 auto', lineHeight: '1.8', fontWeight: '300', padding: '0 1rem' }}>
              Seamless, luxury airport pick-up and drop-off service for your comfort and convenience
            </p>
          </div>

          {/* Feature pills */}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem', marginBottom: 'clamp(3rem, 6vw, 5rem)' }}>
            {['✈️ Airport Transfers', '🕐 24/7 Available', '🌟 Professional Drivers', '❄️ Air Conditioned', '📱 Easy Booking'].map(item => (
              <div key={item} style={{ padding: '0.5rem 1.1rem', background: 'rgba(199, 179, 129, 0.07)', border: '1px solid rgba(199, 179, 129, 0.2)', borderRadius: '20px', color: '#B8B8B8', fontSize: 'clamp(0.75rem, 2vw, 0.85rem)', fontWeight: '500' }}>
                {item}
              </div>
            ))}
          </div>

          {/* Car Grid */}
          {cars.length > 0 && (
            <>
              <h2 style={{ fontSize: 'clamp(1.3rem, 3vw, 1.8rem)', fontWeight: '300', color: '#FFFFFF', marginBottom: 'clamp(1.5rem, 4vw, 2.5rem)', letterSpacing: '-0.01em' }}>
                Available <span style={{ fontWeight: '600', color: '#C7B381' }}>Fleet</span>
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', gap: 'clamp(1.5rem, 4vw, 3rem)', marginBottom: 'clamp(3rem, 8vw, 6rem)' }}>
                {cars.map(car => (
                  <div key={car._id}
                    onMouseEnter={() => setHoveredCard(car._id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{ background: 'rgba(15, 20, 25, 0.6)', border: '1px solid rgba(199, 179, 129, 0.15)', borderRadius: '12px', overflow: 'hidden', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', transform: hoveredCard === car._id ? 'translateY(-12px)' : 'translateY(0)', boxShadow: hoveredCard === car._id ? '0 32px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(199,179,129,0.3)' : '0 8px 32px rgba(0,0,0,0.3)' }}>
                    {/* Image */}
                    <div style={{ position: 'relative', overflow: 'hidden', height: 'clamp(240px, 35vw, 280px)' }}>
                      {car.imageURL ? (
                        <img src={car.imageURL} alt={car.name}
                          onError={e => { e.target.style.display = 'none'; }}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)', transform: hoveredCard === car._id ? 'scale(1.1)' : 'scale(1)' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1A1F2E, #0F1419)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem' }}>🚗</div>
                      )}
                      <div style={{ position: 'absolute', inset: 0, background: hoveredCard === car._id ? 'linear-gradient(to top, rgba(15,20,25,0.8) 0%, transparent 50%)' : 'linear-gradient(to top, rgba(15,20,25,0.6) 0%, transparent 50%)', transition: 'all 0.4s' }} />
                      {/* Category badge */}
                      {car.category && (
                        <div style={{ position: 'absolute', top: 'clamp(12px, 2vw, 18px)', left: 'clamp(12px, 2vw, 18px)', background: 'rgba(199, 179, 129, 0.95)', color: '#0F1419', padding: '0.35rem 0.9rem', borderRadius: '20px', fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)', fontWeight: '700', letterSpacing: '0.05em' }}>
                          {car.category}
                        </div>
                      )}
                      {/* Capacity badge */}
                      {car.capacity && (
                        <div style={{ position: 'absolute', top: 'clamp(12px, 2vw, 18px)', right: 'clamp(12px, 2vw, 18px)', background: 'rgba(15,20,25,0.85)', color: '#C7B381', padding: '0.35rem 0.9rem', borderRadius: '20px', fontSize: 'clamp(0.7rem, 1.5vw, 0.8rem)', fontWeight: '600', border: '1px solid rgba(199,179,129,0.3)' }}>
                          👥 {car.capacity}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ padding: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
                      <h3 style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.7rem)', fontWeight: '400', color: '#FFFFFF', marginBottom: '0.5rem', letterSpacing: '-0.01em' }}>
                        {car.name || 'Airport Vehicle'}
                      </h3>

                      {car.details && (
                        <p style={{ fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)', color: '#888', lineHeight: '1.7', marginBottom: '1rem' }}>
                          {car.details}
                        </p>
                      )}

                      {/* Rate */}
                      <div style={{ padding: 'clamp(1rem, 2vw, 1.5rem) 0', borderTop: '1px solid rgba(199, 179, 129, 0.1)', borderBottom: '1px solid rgba(199, 179, 129, 0.1)', marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: 'clamp(0.7rem, 1.5vw, 0.78rem)', color: '#888', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.4rem' }}>Service Rate</div>
                        <div style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.7rem)', color: '#C7B381', fontWeight: '300' }}>
                          {formatRate(car.rate)}
                        </div>
                      </div>

                      {/* Book button */}
                      <button onClick={() => setSelectedCar(car)}
                        style={{ width: '100%', padding: 'clamp(0.9rem, 2vw, 1.1rem)', background: '#C7B381', color: '#0F1419', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: 'clamp(0.9rem, 1.7vw, 1rem)', cursor: 'pointer', transition: 'all 0.3s', letterSpacing: '0.02em' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#D4C599'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(199,179,129,0.3)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#C7B381'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                        Book This Vehicle
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {cars.length === 0 && (
            <div style={{ textAlign: 'center', padding: 'clamp(60px, 15vw, 120px)', color: '#888' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>✈️</div>
              <h2 style={{ fontSize: 'clamp(1.8rem, 5vw, 2.5rem)', marginBottom: '1rem', color: '#C7B381' }}>No vehicles available</h2>
              <p style={{ fontSize: 'clamp(1rem, 3vw, 1.3rem)' }}>Please check back later</p>
            </div>
          )}

          {/* Notes Section */}
          {notes.length > 0 && (
            <div style={{ marginBottom: 'clamp(3rem, 8vw, 6rem)' }}>
              <h2 style={{ fontSize: 'clamp(1.3rem, 3vw, 1.8rem)', fontWeight: '300', color: '#FFFFFF', marginBottom: 'clamp(1.5rem, 4vw, 2.5rem)', letterSpacing: '-0.01em' }}>
                Service <span style={{ fontWeight: '600', color: '#C7B381' }}>Information</span>
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', gap: 'clamp(1rem, 3vw, 2rem)' }}>
                {notes.map(note => (
                  <div key={note._id} style={{ background: 'rgba(199, 179, 129, 0.04)', border: '1px solid rgba(199, 179, 129, 0.15)', borderRadius: '12px', padding: 'clamp(1.4rem, 3vw, 2rem)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.8rem', marginBottom: '0.8rem' }}>
                      <span style={{ fontSize: '1.2rem', marginTop: '0.1rem' }}>📌</span>
                      <h4 style={{ fontSize: 'clamp(1rem, 2vw, 1.1rem)', fontWeight: '600', color: '#C7B381', lineHeight: '1.4' }}>
                        {note.title || 'Service Note'}
                      </h4>
                    </div>
                    <p style={{ fontSize: 'clamp(0.85rem, 1.6vw, 0.95rem)', color: '#B8B8B8', lineHeight: '1.8', whiteSpace: 'pre-wrap', paddingLeft: '2rem' }}>
                      {note.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedCar && (
        <div onClick={() => setSelectedCar(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 'clamp(1rem, 2vw, 2rem)', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'linear-gradient(135deg, #1A1F2E 0%, #0F1419 100%)', border: '1px solid rgba(199, 179, 129, 0.3)', borderRadius: '16px', width: '100%', maxWidth: '820px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 48px rgba(0,0,0,0.6)', margin: isMobile ? '0' : '2rem auto' }}>
            <div style={{ padding: 'clamp(1.5rem, 4vw, 2.5rem)' }}>
              <div style={{ marginBottom: 'clamp(1.5rem, 3vw, 2rem)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <div>
                  <h3 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#FFFFFF', marginBottom: '0.4rem', fontWeight: '400' }}>{selectedCar.name}</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {selectedCar.category && <span style={{ padding: '0.3rem 0.8rem', background: 'rgba(199,179,129,0.15)', border: '1px solid rgba(199,179,129,0.3)', borderRadius: '20px', color: '#C7B381', fontSize: 'clamp(0.72rem, 1.5vw, 0.82rem)', fontWeight: '600' }}>{selectedCar.category}</span>}
                    {selectedCar.capacity && <span style={{ padding: '0.3rem 0.8rem', background: 'rgba(199,179,129,0.08)', border: '1px solid rgba(199,179,129,0.2)', borderRadius: '20px', color: '#B8B8B8', fontSize: 'clamp(0.72rem, 1.5vw, 0.82rem)', fontWeight: '500' }}>👥 {selectedCar.capacity}</span>}
                  </div>
                </div>
                <button onClick={() => setSelectedCar(null)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#888', borderRadius: '8px', width: '36px', height: '36px', cursor: 'pointer', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
              </div>

              {/* Image */}
              {selectedCar.imageURL && (
                <div style={{ marginBottom: 'clamp(1.5rem, 3vw, 2rem)', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(199,179,129,0.2)' }}>
                  <img src={selectedCar.imageURL} alt={selectedCar.name} style={{ width: '100%', height: isMobile ? '200px' : '280px', objectFit: 'cover' }} />
                </div>
              )}

              {/* Rate */}
              <div style={{ background: 'rgba(199,179,129,0.06)', border: '1px solid rgba(199,179,129,0.2)', borderRadius: '12px', padding: 'clamp(1.2rem, 3vw, 1.8rem)', marginBottom: 'clamp(1.2rem, 3vw, 1.8rem)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: 'clamp(0.7rem, 1.4vw, 0.78rem)', color: '#888', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.4rem' }}>Service Rate</div>
                  <div style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#C7B381', fontWeight: '300' }}>{formatRate(selectedCar.rate)}</div>
                </div>
                <div style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.85rem)', color: '#666', maxWidth: '220px', lineHeight: '1.6' }}>
                  Rate includes GST for airport pick-up and drop-off
                </div>
              </div>

              {/* Details */}
              {selectedCar.details && (
                <div style={{ marginBottom: 'clamp(1.2rem, 3vw, 1.8rem)', padding: 'clamp(1.2rem, 3vw, 1.8rem)', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(199,179,129,0.1)', borderRadius: '12px' }}>
                  <div style={{ fontSize: 'clamp(0.7rem, 1.4vw, 0.78rem)', color: '#888', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600', marginBottom: '0.6rem' }}>Vehicle Details</div>
                  <p style={{ color: '#B8B8B8', fontSize: 'clamp(0.88rem, 1.6vw, 0.97rem)', lineHeight: '1.8' }}>{selectedCar.details}</p>
                </div>
              )}

              {/* Contact CTA */}
              <div style={{ padding: 'clamp(1.2rem, 3vw, 1.8rem)', background: 'rgba(199,179,129,0.06)', border: '1px solid rgba(199,179,129,0.2)', borderRadius: '12px', marginBottom: 'clamp(1rem, 2vw, 1.5rem)' }}>
                <h4 style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', color: '#C7B381', marginBottom: '0.5rem', fontWeight: '500' }}>Ready to book?</h4>
                <p style={{ color: '#B8B8B8', fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)', lineHeight: '1.6', marginBottom: 'clamp(1rem, 2vw, 1.3rem)' }}>Contact us to confirm availability and schedule your transfer</p>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '0.8rem' }}>
                  <a href={`tel:${PHONE_NUMBER}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: 'clamp(0.9rem, 2vw, 1rem)', background: '#4CAF50', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '600', fontSize: 'clamp(0.88rem, 1.6vw, 0.95rem)', transition: 'all 0.3s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#5FBF6F'}
                    onMouseLeave={e => e.currentTarget.style.background = '#4CAF50'}>
                    📞 Call Now
                  </a>
                  <a href={`https://wa.me/${PHONE_NUMBER.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: 'clamp(0.9rem, 2vw, 1rem)', background: '#25D366', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '600', fontSize: 'clamp(0.88rem, 1.6vw, 0.95rem)', transition: 'all 0.3s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#34E376'}
                    onMouseLeave={e => e.currentTarget.style.background = '#25D366'}>
                    💬 WhatsApp
                  </a>
                </div>
              </div>

              {/* Close */}
              <button onClick={() => setSelectedCar(null)}
                style={{ width: '100%', padding: 'clamp(0.9rem, 2vw, 1.1rem)', background: 'transparent', color: '#C7B381', border: '1px solid rgba(199,179,129,0.4)', borderRadius: '8px', fontWeight: '600', fontSize: 'clamp(0.9rem, 1.7vw, 1rem)', cursor: 'pointer', transition: 'all 0.3s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(199,179,129,0.1)'; e.currentTarget.style.borderColor = '#C7B381'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(199,179,129,0.4)'; }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AirportServiceViewer;